import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ClientFormDialog from "@/components/ClientFormDialog";
import { QuickAssignDialog } from "@/components/QuickAssignDialog";
import { ClientFilters } from "@/components/ClientFilters";
import { ClientTable } from "@/components/ClientTable";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, Download, UserCheck } from "lucide-react";
import { toast } from "sonner";

export default function Clients() {
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [tag, setTag] = useState("all");
  const [mine, setMine] = useState(searchParams.get("mine") === "1");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (status !== "all") params.status = status;
    if (tag !== "all") params.tag = tag;
    if (mine) params.mine = true;
    const { data } = await api.get("/clients", { params });
    setClients(data);
    setLoading(false);
  }, [search, status, tag, mine]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/tags").then((r) => setTags(r.data)); }, [dialogOpen]);

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setDialogOpen(true); };

  const confirmDelete = async () => {
    await api.delete(`/clients/${deleteTarget.id}`);
    toast.success("Client deleted");
    setDeleteTarget(null);
    load();
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/clients-export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "clients.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported CSV");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="clients-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-gray-900">Clients</h1>
          <p className="text-gray-500 text-sm mt-1">{clients.length} record{clients.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setMine((m) => !m)} data-testid="my-clients-toggle"
            className={`flex items-center gap-1.5 px-4 py-2 rounded-sm font-semibold text-sm transition-colors border ${mine ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}>
            <UserCheck className="w-4 h-4" /> My clients
          </button>
          <button onClick={exportCsv} data-testid="export-csv-button"
            className="flex items-center gap-1.5 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm transition-colors">
            <Download className="w-4 h-4" /> Export
          </button>
          <button onClick={openNew} data-testid="add-client-button"
            className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm transition-colors">
            <Plus className="w-4 h-4" /> New client
          </button>
        </div>
      </div>

      <ClientFilters search={search} setSearch={setSearch} status={status} setStatus={setStatus}
        tag={tag} setTag={setTag} tags={tags} />

      <ClientTable clients={clients} loading={loading} onEdit={openEdit} onDelete={setDeleteTarget} onAddNew={openNew} onAssign={setAssignTarget} />

      <ClientFormDialog open={dialogOpen} onOpenChange={setDialogOpen} client={editing} onSaved={load} />

      <QuickAssignDialog open={Boolean(assignTarget)} onOpenChange={(o) => !o && setAssignTarget(null)} client={assignTarget} onSaved={load} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete client?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <strong>{deleteTarget?.name}</strong> and all associated notes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-button" className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
